import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, FileSpreadsheet, Layers, Plus, RotateCcw, Save, Trash2, Pencil, Upload } from 'lucide-react';
import { CustomDeck, MasterList, TournamentTrick, User } from '../types';
import { DEFAULT_DRAW_PRESET_DECKS, DEFAULT_TOURNAMENT_MASTER_LISTS, PRESET_FOLDERS, fetchManagedDrawPresetDecks, fetchManagedTournamentMasterLists } from '../storage';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from '../apiEnv';
import * as XLSX from 'xlsx';

const MANAGED_MASTER_LISTS_VERSION_KEY = 'kendama-managed-master-lists-version';
const MANAGED_DRAW_PRESET_DECKS_VERSION_KEY = 'kendama-managed-draw-preset-decks-version';

interface AdminTournamentMasterListsProps {
  user: User;
  mode: 'points' | 'draw';
}

const cloneMasterLists = (lists: MasterList[]) => lists.map(list => ({
  ...list,
  tricks: Array.isArray(list.tricks) ? list.tricks.map(trick => ({ ...trick })) : []
}));

const serializeMasterLists = (lists: MasterList[]) => JSON.stringify(lists);

const cloneDrawPresetDecks = (decks: CustomDeck[]) => decks.map(deck => ({
  ...deck,
  tricks: Array.isArray(deck.tricks) ? deck.tricks.map(trick => String(trick)) : []
}));

const serializeDrawPresetDecks = (decks: CustomDeck[]) => JSON.stringify(decks);

const getPreferredAdminMasterListId = (lists: MasterList[], preferredId?: string) => {
  if (preferredId && lists.some(list => list.id === preferredId)) {
    return preferredId;
  }

  return (
    lists.find(list => list.id !== 'default' && Array.isArray(list.tricks) && list.tricks.length > 0)?.id ||
    lists.find(list => list.id !== 'default')?.id ||
    lists[0]?.id ||
    'default'
  );
};

const getReferencedTournamentMasterListId = (user: User, lists: MasterList[]) => {
  const tournamentData = user.tournamentData;
  if (!tournamentData) return undefined;

  const referencedIds = [
    ...tournamentData.prelim1Configs.flatMap(config => [
      ...config.selectedTricks.map(reference => reference.masterListId),
      ...(config.alternativeTrick ? [config.alternativeTrick.masterListId] : [])
    ]),
    ...tournamentData.prelim2Configs.flatMap(config => [
      ...config.selectedTricks.map(reference => reference.masterListId),
      ...(config.alternativeTrick ? [config.alternativeTrick.masterListId] : [])
    ]),
    ...tournamentData.finalConfigs.flatMap(config => [
      ...config.selectedTricks.map(reference => reference.masterListId),
      ...(config.alternativeTrick ? [config.alternativeTrick.masterListId] : [])
    ])
  ];

  return referencedIds.find(listId => lists.some(list => list.id === listId));
};

const AdminTournamentMasterLists: React.FC<AdminTournamentMasterListsProps> = ({ user, mode }) => {
  const [masterLists, setMasterLists] = useState<MasterList[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState<MasterList[]>([]);
  const [selectedMasterListId, setSelectedMasterListId] = useState('default');
  const [newListName, setNewListName] = useState('');
  const [newTrickName, setNewTrickName] = useState('');
  const [newTrickScore, setNewTrickScore] = useState('');
  const [editingTrickId, setEditingTrickId] = useState<string | null>(null);
  const [editingTrickName, setEditingTrickName] = useState('');
  const [editingTrickScore, setEditingTrickScore] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [expandedScoreGroups, setExpandedScoreGroups] = useState<Record<string, boolean>>({});
  const [drawPresetDecks, setDrawPresetDecks] = useState<CustomDeck[]>([]);
  const [drawSavedSnapshot, setDrawSavedSnapshot] = useState<CustomDeck[]>([]);
  const [selectedDrawDeckId, setSelectedDrawDeckId] = useState('');
  const [newDrawDeckName, setNewDrawDeckName] = useState('');
  const [newDrawDeckFolderId, setNewDrawDeckFolderId] = useState(PRESET_FOLDERS[0]?.id || '');
  const [newDrawTrickName, setNewDrawTrickName] = useState('');
  const [drawSaving, setDrawSaving] = useState(false);
  const [drawSaveMessage, setDrawSaveMessage] = useState('');
  const [expandedDrawFolders, setExpandedDrawFolders] = useState<Record<string, boolean>>({});
  const [editingDrawTrickIndex, setEditingDrawTrickIndex] = useState<number | null>(null);
  const [editingDrawTrickName, setEditingDrawTrickName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;

    const loadMasterLists = async () => {
      setLoading(true);
      const [remoteLists, remoteDrawPresetDecks] = await Promise.all([
        fetchManagedTournamentMasterLists(),
        fetchManagedDrawPresetDecks()
      ]);
      const initialLists = remoteLists && remoteLists.length > 0
        ? cloneMasterLists(remoteLists)
        : cloneMasterLists(DEFAULT_TOURNAMENT_MASTER_LISTS);
      const initialDrawPresetDecks = remoteDrawPresetDecks && remoteDrawPresetDecks.length > 0
        ? cloneDrawPresetDecks(remoteDrawPresetDecks)
        : cloneDrawPresetDecks(DEFAULT_DRAW_PRESET_DECKS);

      if (!mounted) return;

      setMasterLists(initialLists);
      setSavedSnapshot(cloneMasterLists(initialLists));
      setSelectedMasterListId(
        getPreferredAdminMasterListId(initialLists, getReferencedTournamentMasterListId(user, initialLists))
      );
      setDrawPresetDecks(initialDrawPresetDecks);
      setDrawSavedSnapshot(cloneDrawPresetDecks(initialDrawPresetDecks));
      setSelectedDrawDeckId(initialDrawPresetDecks[0]?.id || '');
      setLoading(false);
    };

    loadMasterLists();
    return () => {
      mounted = false;
    };
  }, [user]);

  const selectedMasterList = useMemo(
    () => masterLists.find(list => list.id === selectedMasterListId) || masterLists[0],
    [masterLists, selectedMasterListId]
  );

  const selectedDrawDeck = useMemo(
    () => drawPresetDecks.find(deck => deck.id === selectedDrawDeckId) || drawPresetDecks[0],
    [drawPresetDecks, selectedDrawDeckId]
  );

  const groupedDrawPresetDecks = useMemo(() => {
    const folderMap = new Map(PRESET_FOLDERS.map(folder => [folder.id, { ...folder, decks: [] as CustomDeck[] }]));

    drawPresetDecks.forEach(deck => {
      const folderId = deck.folderId || PRESET_FOLDERS[0]?.id || 'ungrouped';
      if (!folderMap.has(folderId)) {
        folderMap.set(folderId, {
          id: folderId,
          name: folderId,
          decks: []
        });
      }
      folderMap.get(folderId)?.decks.push(deck);
    });

    return Array.from(folderMap.values()).filter(group => group.decks.length > 0);
  }, [drawPresetDecks]);

  const toggleDrawFolder = (folderId: string) => {
    setExpandedDrawFolders(previous => ({
      ...previous,
      [folderId]: !previous[folderId]
    }));
  };

  const trickGroups = useMemo(() => {
    if (!selectedMasterList) {
      return [];
    }

    const groups = new Map<number, TournamentTrick[]>();
    selectedMasterList.tricks.forEach(trick => {
      const existingGroup = groups.get(trick.score) || [];
      existingGroup.push(trick);
      groups.set(trick.score, existingGroup);
    });

    return Array.from(groups.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([score, tricks]) => ({ score, tricks }));
  }, [selectedMasterList]);

  const toggleScoreGroup = (score: number) => {
    const groupKey = `${selectedMasterListId}:${score}`;
    setExpandedScoreGroups(previous => ({
      ...previous,
      [groupKey]: !previous[groupKey]
    }));
  };

  const masterListsWithPendingEdit = useMemo(() => {
    if (!selectedMasterList || !editingTrickId) {
      return masterLists;
    }

    return masterLists.map(list => {
      if (list.id !== selectedMasterList.id) {
        return list;
      }

      return {
        ...list,
        tricks: list.tricks.map(trick => (
          trick.id === editingTrickId
            ? { ...trick, name: editingTrickName, score: Number(editingTrickScore) }
            : trick
        ))
      };
    });
  }, [masterLists, selectedMasterList, editingTrickId, editingTrickName, editingTrickScore]);

  const hasPendingEditingDraft = useMemo(() => {
    if (!selectedMasterList || !editingTrickId) {
      return false;
    }

    const originalTrick = selectedMasterList.tricks.find(trick => trick.id === editingTrickId);
    if (!originalTrick) {
      return false;
    }

    return (
      originalTrick.name !== editingTrickName ||
      String(originalTrick.score) !== editingTrickScore
    );
  }, [selectedMasterList, editingTrickId, editingTrickName, editingTrickScore]);

  const drawPresetDecksWithPendingEdit = useMemo(() => {
    if (!selectedDrawDeck || editingDrawTrickIndex === null) {
      return drawPresetDecks;
    }

    return drawPresetDecks.map(deck => {
      if (deck.id !== selectedDrawDeck.id) {
        return deck;
      }

      return {
        ...deck,
        tricks: deck.tricks.map((trick, index) => (
          index === editingDrawTrickIndex ? editingDrawTrickName : trick
        ))
      };
    });
  }, [drawPresetDecks, selectedDrawDeck, editingDrawTrickIndex, editingDrawTrickName]);


  const selectedDrawDeckWithPendingEdit = useMemo(
    () => drawPresetDecksWithPendingEdit.find(deck => deck.id === selectedDrawDeckId) || drawPresetDecksWithPendingEdit[0],
    [drawPresetDecksWithPendingEdit, selectedDrawDeckId]
  );

  const hasPendingDrawEditingDraft = useMemo(() => {
    if (!selectedDrawDeck || editingDrawTrickIndex === null) {
      return false;
    }

    const originalTrick = selectedDrawDeck.tricks[editingDrawTrickIndex];
    if (typeof originalTrick !== 'string') {
      return false;
    }

    return originalTrick !== editingDrawTrickName;
  }, [selectedDrawDeck, editingDrawTrickIndex, editingDrawTrickName]);

  const hasUnsavedChanges = useMemo(
    () => (
      serializeMasterLists(masterLists) !== serializeMasterLists(savedSnapshot) ||
      hasPendingEditingDraft
    ),
    [masterLists, savedSnapshot, hasPendingEditingDraft]
  );

  const hasDrawUnsavedChanges = useMemo(
    () => (
      serializeDrawPresetDecks(drawPresetDecksWithPendingEdit) !== serializeDrawPresetDecks(drawSavedSnapshot) ||
      hasPendingDrawEditingDraft
    ),
    [drawPresetDecksWithPendingEdit, drawSavedSnapshot, hasPendingDrawEditingDraft]
  );

  const updateSelectedList = (updater: (list: MasterList) => MasterList) => {
    if (!selectedMasterList) return;
    setMasterLists(prev => prev.map(list => list.id === selectedMasterList.id ? updater(list) : list));
  };

  const updateSelectedDrawDeck = (updater: (deck: CustomDeck) => CustomDeck) => {
    if (!selectedDrawDeck) return;
    setDrawPresetDecks(prev => prev.map(deck => deck.id === selectedDrawDeck.id ? updater(deck) : deck));
  };

  const handleCreateList = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListName.trim()) return;

    const newList: MasterList = {
      id: `managed-${Date.now()}`,
      name: newListName.trim(),
      isPreset: true,
      tricks: []
    };

    setMasterLists(prev => [...prev, newList]);
    setSelectedMasterListId(newList.id);
    setNewListName('');
    setSaveMessage('');
  };

  const handleDeleteList = (listId: string) => {
    if (listId === 'default') {
      alert('Default list must be kept. / 默认总表必须保留。');
      return;
    }
    if (!confirm('Delete this global master list? / 删除这个全局积分赛总表？')) return;

    const remainingLists = masterLists.filter(list => list.id !== listId);
    setMasterLists(remainingLists);
    setSelectedMasterListId(getPreferredAdminMasterListId(remainingLists));
    setSaveMessage('');
  };

  const handleCreateDrawDeck = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDrawDeckName.trim()) return;

    const newDeck: CustomDeck = {
      id: `managed-draw-${Date.now()}`,
      name: newDrawDeckName.trim(),
      folderId: newDrawDeckFolderId || PRESET_FOLDERS[0]?.id,
      isPreset: true,
      tricks: []
    };

    setDrawPresetDecks(prev => [...prev, newDeck]);
    setSelectedDrawDeckId(newDeck.id);
    setExpandedDrawFolders(previous => ({
      ...previous,
      [newDeck.folderId || PRESET_FOLDERS[0]?.id || 'ungrouped']: true
    }));
    setNewDrawDeckName('');
    setDrawSaveMessage('');
  };

  const handleDeleteDrawDeck = (deckId: string) => {
    if (!confirm('Delete this global draw preset deck? / 删除这个全局抽招默认总表？')) return;

    const remainingDecks = drawPresetDecks.filter(deck => deck.id !== deckId);
    setDrawPresetDecks(remainingDecks);
    setSelectedDrawDeckId(remainingDecks[0]?.id || '');
    setEditingDrawTrickIndex(null);
    setEditingDrawTrickName('');
    setDrawSaveMessage('');
  };

  const handleAddTrick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMasterList || !newTrickName.trim() || !newTrickScore.trim()) return;

    const score = Number(newTrickScore);
    if (!Number.isFinite(score)) {
      alert('Please enter a valid score. / 请输入有效分数。');
      return;
    }

    const newTrick: TournamentTrick = {
      id: `${selectedMasterList.id}-trick-${Date.now()}`,
      name: newTrickName.trim(),
      score
    };

    updateSelectedList(list => ({ ...list, tricks: [...list.tricks, newTrick] }));
    setNewTrickName('');
    setNewTrickScore('');
    setSaveMessage('');
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedMasterList) return;

    const reader = new FileReader();
    reader.onload = uploadEvent => {
      try {
        const buffer = uploadEvent.target?.result;
        if (!buffer) {
          alert('Failed to read the Excel file. / 读取 Excel 文件失败。');
          return;
        }

        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as Array<Array<unknown>>;

        const importedTricks: TournamentTrick[] = [];
        rows.forEach((row, rowIndex) => {
          if (!Array.isArray(row) || row.length < 2) return;

          const name = String(row[0] ?? '').trim();
          const rawScore = String(row[1] ?? '').replace(/[^0-9.-]+/g, '');
          const score = Number(rawScore);

          if (!name || !Number.isFinite(score)) return;
          if (rowIndex === 0 && /score|分数/i.test(rawScore) && /name|招式/i.test(name)) return;

          importedTricks.push({
            id: `${selectedMasterList.id}-excel-${Date.now()}-${rowIndex}`,
            name,
            score
          });
        });

        if (importedTricks.length === 0) {
          alert('No valid tricks found in the Excel file. Use column A for trick name and column B for score. / Excel 中没有读取到有效招式，请使用第一列填写招式名称、第二列填写分数。');
          return;
        }

        updateSelectedList(list => ({
          ...list,
          tricks: [...list.tricks, ...importedTricks]
        }));
        setSaveMessage(`Imported ${importedTricks.length} tricks from Excel. Remember to save changes. / 已从 Excel 导入 ${importedTricks.length} 个招式，请记得保存更改。`);
      } catch (error) {
        console.error(error);
        alert('Failed to parse the Excel file. / Excel 解析失败。');
      } finally {
        event.target.value = '';
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const startEditingTrick = (trick: TournamentTrick) => {
    setEditingTrickId(trick.id);
    setEditingTrickName(trick.name);
    setEditingTrickScore(String(trick.score));
  };

  const handleSaveTrick = () => {
    if (!selectedMasterList || !editingTrickId || !editingTrickName.trim() || !editingTrickScore.trim()) return;

    const score = Number(editingTrickScore);
    if (!Number.isFinite(score)) {
      alert('Please enter a valid score. / 请输入有效分数。');
      return;
    }

    updateSelectedList(list => ({
      ...list,
      tricks: list.tricks.map(trick => (
        trick.id === editingTrickId
          ? { ...trick, name: editingTrickName.trim(), score }
          : trick
      ))
    }));

    setEditingTrickId(null);
    setEditingTrickName('');
    setEditingTrickScore('');
    setSaveMessage('');
  };

  const applyPendingEditingTrick = () => {
    if (!selectedMasterList || !editingTrickId) {
      return masterLists;
    }

    if (!editingTrickName.trim() || !editingTrickScore.trim()) {
      throw new Error('Please complete the trick name and score before saving. / 保存前请填写完整的招式名称和分数。');
    }

    const score = Number(editingTrickScore);
    if (!Number.isFinite(score)) {
      throw new Error('Please enter a valid score. / 请输入有效分数。');
    }

    return masterLists.map(list => {
      if (list.id !== selectedMasterList.id) {
        return list;
      }

      return {
        ...list,
        tricks: list.tricks.map(trick => (
          trick.id === editingTrickId
            ? { ...trick, name: editingTrickName.trim(), score }
            : trick
        ))
      };
    });
  };

  const handleDeleteTrick = (trickId: string) => {
    if (!selectedMasterList) return;
    if (!confirm('Delete this trick? / 删除这个招式？')) return;

    updateSelectedList(list => ({
      ...list,
      tricks: list.tricks.filter(trick => trick.id !== trickId)
    }));
    setSaveMessage('');
  };

  const handleReset = () => {
    setMasterLists(cloneMasterLists(savedSnapshot));
    setSelectedMasterListId(getPreferredAdminMasterListId(savedSnapshot, selectedMasterListId));
    setEditingTrickId(null);
    setSaveMessage('');
  };

  const handleAddDrawTrick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDrawDeck || !newDrawTrickName.trim()) return;

    updateSelectedDrawDeck(deck => ({
      ...deck,
      tricks: [...deck.tricks, newDrawTrickName.trim()]
    }));
    setNewDrawTrickName('');
    setDrawSaveMessage('');
  };

  const handleResetDraw = () => {
    setDrawPresetDecks(cloneDrawPresetDecks(drawSavedSnapshot));
    setSelectedDrawDeckId(
      drawSavedSnapshot.find(deck => deck.id === selectedDrawDeckId)?.id || drawSavedSnapshot[0]?.id || ''
    );
    setEditingDrawTrickIndex(null);
    setEditingDrawTrickName('');
    setDrawSaveMessage('');
  };

  const startEditingDrawTrick = (trickIndex: number, trickName: string) => {
    setEditingDrawTrickIndex(trickIndex);
    setEditingDrawTrickName(trickName);
  };

  const handleSaveDrawTrick = () => {
    if (!selectedDrawDeck || editingDrawTrickIndex === null || !editingDrawTrickName.trim()) return;

    updateSelectedDrawDeck(deck => ({
      ...deck,
      tricks: deck.tricks.map((trick, index) => (
        index === editingDrawTrickIndex ? editingDrawTrickName.trim() : trick
      ))
    }));

    setEditingDrawTrickIndex(null);
    setEditingDrawTrickName('');
    setDrawSaveMessage('');
  };

  const applyPendingEditingDrawTrick = () => {
    if (!selectedDrawDeck || editingDrawTrickIndex === null) {
      return drawPresetDecks;
    }

    if (!editingDrawTrickName.trim()) {
      throw new Error('Please complete the trick name before saving. / 保存前请填写完整的招式名称。');
    }

    return drawPresetDecks.map(deck => {
      if (deck.id !== selectedDrawDeck.id) {
        return deck;
      }

      return {
        ...deck,
        tricks: deck.tricks.map((trick, index) => (
          index === editingDrawTrickIndex ? editingDrawTrickName.trim() : trick
        ))
      };
    });
  };

  const handleDeleteDrawTrick = (trickIndex: number) => {
    if (!selectedDrawDeck) return;
    if (!confirm('Delete this trick? / 删除这个招式？')) return;

    updateSelectedDrawDeck(deck => ({
      ...deck,
      tricks: deck.tricks.filter((_, index) => index !== trickIndex)
    }));

    if (editingDrawTrickIndex === trickIndex) {
      setEditingDrawTrickIndex(null);
      setEditingDrawTrickName('');
    } else if (editingDrawTrickIndex !== null && editingDrawTrickIndex > trickIndex) {
      setEditingDrawTrickIndex(editingDrawTrickIndex - 1);
    }

    setDrawSaveMessage('');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setSaveMessage('');

    try {
      const payloadMasterLists = applyPendingEditingTrick();
      const response = await fetch(
        isLocalBackend()
          ? '/api/admin/tournament-master-lists'
          : phpApiUrl('admin_save_tournament_master_lists'),
        {
          method: 'POST',
          headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({ masterLists: payloadMasterLists })
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save master lists');
      }

      const savedLists = Array.isArray(data.masterLists) && data.masterLists.length > 0
        ? cloneMasterLists(data.masterLists)
        : cloneMasterLists(payloadMasterLists);

      setMasterLists(savedLists);
      setSavedSnapshot(cloneMasterLists(savedLists));
      setSelectedMasterListId(getPreferredAdminMasterListId(savedLists, selectedMasterListId));
      setEditingTrickId(null);
      setEditingTrickName('');
      setEditingTrickScore('');
      try {
        localStorage.setItem(MANAGED_MASTER_LISTS_VERSION_KEY, String(Date.now()));
      } catch (error) {
        console.error('Failed to broadcast managed master list update:', error);
      }
      setSaveMessage('Global master lists saved. Refresh tournament pages to apply the latest defaults. / 默认积分赛总表已保存，刷新积分赛页面后会应用最新默认总表。');
    } catch (error: any) {
      alert(error.message || 'Failed to save master lists / 保存总表失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDrawPresetDecks = async () => {
    setDrawSaving(true);
    setDrawSaveMessage('');

    try {
      const payloadDrawPresetDecks = applyPendingEditingDrawTrick();
      const response = await fetch(
        isLocalBackend()
          ? '/api/admin/draw-preset-decks'
          : phpApiUrl('admin_save_draw_preset_decks'),
        {
          method: 'POST',
          headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({ presetDecks: payloadDrawPresetDecks })
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save draw preset decks');
      }

      const savedDecks = Array.isArray(data.presetDecks) && data.presetDecks.length > 0
        ? cloneDrawPresetDecks(data.presetDecks)
        : cloneDrawPresetDecks(payloadDrawPresetDecks);

      setDrawPresetDecks(savedDecks);
      setDrawSavedSnapshot(cloneDrawPresetDecks(savedDecks));
      setSelectedDrawDeckId(savedDecks.find(deck => deck.id === selectedDrawDeckId)?.id || savedDecks[0]?.id || '');
      setEditingDrawTrickIndex(null);
      setEditingDrawTrickName('');
      try {
        localStorage.setItem(MANAGED_DRAW_PRESET_DECKS_VERSION_KEY, String(Date.now()));
      } catch (error) {
        console.error('Failed to broadcast managed draw preset deck update:', error);
      }
      setDrawSaveMessage('Global draw preset decks saved. Refresh draw battle pages to apply the latest defaults. / 抽招赛默认总表已保存，刷新抽招赛页面后会应用最新默认总表。');
    } catch (error: any) {
      alert(error.message || 'Failed to save draw preset decks / 保存抽招赛默认总表失败');
    } finally {
      setDrawSaving(false);
    }
  };

  if (!user.isAdmin) return null;

  return (
    <div className="w-full min-w-0 overflow-x-hidden space-y-4 md:space-y-6">
      {mode === 'points' && (
      <>
      <div className="rounded-2xl border border-cyan-500/20 bg-black/40 p-4 md:p-5">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-cyan-300 font-orbitron text-sm tracking-[0.22em] uppercase">
              <Layers size={16} />
              Point Master Lists / 积分总表
            </div>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              Manage the global default master lists used by Point Tournament mode. Changes here become the shared default source lists for all users. / 在这里管理积分赛全局默认总表，保存后会成为所有用户共用的默认积分赛总表来源。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleReset}
              disabled={!hasUnsavedChanges || saving}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-orbitron text-xs uppercase tracking-wider"
            >
              <RotateCcw size={14} />
              Reset / 重置
            </button>
            <button
              onClick={handleSaveAll}
              disabled={!hasUnsavedChanges || saving}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-cyan-400/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-orbitron text-xs uppercase tracking-wider"
            >
              <Save size={14} />
              {saving ? 'Saving / 保存中' : 'Save Changes / 保存更改'}
            </button>
          </div>
        </div>
        {saveMessage && (
          <div className="mt-4 rounded-xl border border-green-500/25 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0" />
            <span>{saveMessage}</span>
          </div>
        )}
      </div>

      <div className="grid min-w-0 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <div className="min-w-0 rounded-2xl border border-white/10 bg-black/35 p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm md:text-base font-orbitron tracking-[0.2em] uppercase text-white">Global Lists / 全局总表</h2>
            <span className="text-[10px] text-zinc-500 font-orbitron tracking-[0.18em] uppercase">{masterLists.length} Lists</span>
          </div>

          <form onSubmit={handleCreateList} className="flex gap-2">
            <input
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              placeholder="New master list... / 新总表..."
              className="flex-1 rounded-lg border border-white/10 bg-black/50 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
            />
            <button
              type="submit"
              className="px-3 py-2 rounded-lg border border-cyan-400/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 transition-colors"
              title="Create list"
            >
              <Plus size={16} />
            </button>
          </form>

          <div className="space-y-2 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
            {loading ? (
              <div className="py-10 text-center text-zinc-500 font-orbitron text-sm">Loading master lists...</div>
            ) : masterLists.length === 0 ? (
              <div className="py-10 text-center text-zinc-500 font-orbitron text-sm">No master lists found.</div>
            ) : masterLists.map(list => (
              <button
                key={list.id}
                onClick={() => setSelectedMasterListId(list.id)}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                  selectedMasterList?.id === list.id
                    ? 'border-cyan-400/40 bg-cyan-500/10'
                    : 'border-white/8 bg-white/[0.03] hover:border-cyan-400/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-rajdhani font-bold truncate">{list.name}</div>
                    <div className="mt-1 text-[10px] font-orbitron uppercase tracking-[0.18em] text-zinc-500">
                      {list.id === 'default' ? 'Default Base / 默认基础表' : 'Global Default / 全局默认'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-cyan-300 font-orbitron text-xs">{list.tricks.length}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-[0.14em]">Tricks</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="min-w-0 rounded-2xl border border-white/10 bg-black/35 p-4 md:p-5 space-y-4">
          {!selectedMasterList ? (
            <div className="py-16 text-center text-zinc-500 font-orbitron">Select a master list / 请选择一个总表</div>
          ) : (
            <>
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-zinc-500">List Name / 总表名称</div>
                  <input
                    value={selectedMasterList.name}
                    onChange={e => updateSelectedList(list => ({ ...list, name: e.target.value }))}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-white font-rajdhani text-xl outline-none focus:border-cyan-400/60"
                  />
                </div>
                <button
                  onClick={() => handleDeleteList(selectedMasterList.id)}
                  disabled={selectedMasterList.id === 'default'}
                  className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-35 disabled:cursor-not-allowed transition-colors font-orbitron text-xs uppercase tracking-wider"
                >
                  <Trash2 size={14} />
                  Delete List / 删除总表
                </button>
              </div>

              <form onSubmit={handleAddTrick} className="rounded-2xl border border-cyan-500/15 bg-cyan-500/[0.05] p-4">
                <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-cyan-300/80">Add Trick / 新增招式</div>
                <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_140px_auto]">
                  <input
                    value={newTrickName}
                    onChange={e => setNewTrickName(e.target.value)}
                    placeholder="Trick name / 招式名称"
                    className="rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-white outline-none focus:border-cyan-400/60"
                  />
                  <input
                    value={newTrickScore}
                    onChange={e => setNewTrickScore(e.target.value)}
                    placeholder="Score / 分数"
                    className="rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-white outline-none focus:border-cyan-400/60"
                  />
                  <button
                    type="submit"
                    className="rounded-xl border border-cyan-400/35 bg-cyan-500/15 px-4 py-3 text-cyan-200 hover:bg-cyan-500/25 transition-colors font-orbitron text-xs uppercase tracking-wider"
                  >
                    Add / 添加
                  </button>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-3 text-fuchsia-200 hover:bg-fuchsia-500/20 transition-colors font-orbitron text-xs uppercase tracking-wider"
                  >
                    <Upload size={14} />
                    Import Excel / 导入 Excel
                  </button>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-400 leading-relaxed">
                    <FileSpreadsheet size={14} className="shrink-0 text-fuchsia-300/80" />
                    <span>Use column A for trick name and column B for score. / 第一列填招式名称，第二列填分数。</span>
                  </div>
                </div>
              </form>

              <div className="min-w-0 space-y-5">
                {selectedMasterList.tricks.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] py-12 text-center text-zinc-500 font-orbitron text-sm">
                    No tricks in this list yet. / 当前总表还没有招式。
                  </div>
                ) : trickGroups.map(group => {
                  const groupKey = `${selectedMasterListId}:${group.score}`;
                  const isExpanded = !!expandedScoreGroups[groupKey];

                  return (
                    <section key={`score-${group.score}`} className="min-w-0 overflow-hidden rounded-3xl border border-white/8 bg-white/[0.02] p-4 md:p-5">
                      <button
                        type="button"
                        onClick={() => toggleScoreGroup(group.score)}
                        className="mb-1 flex w-full min-w-0 flex-col gap-2 rounded-2xl border border-white/8 bg-white/[0.02] px-4 py-3 text-left transition-colors hover:border-cyan-400/20 hover:bg-white/[0.035] sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="inline-flex max-w-full flex-wrap items-center gap-2 rounded-2xl border border-yellow-400/20 bg-yellow-500/10 px-3 py-2 sm:w-fit sm:flex-nowrap sm:gap-3 sm:px-4">
                          {isExpanded ? (
                            <ChevronDown size={16} className="text-yellow-300" />
                          ) : (
                            <ChevronRight size={16} className="text-yellow-300" />
                          )}
                          <span className="font-orbitron text-sm sm:text-base text-yellow-300">{group.score} pts</span>
                          <span className="font-orbitron text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.18em] text-yellow-100/65">
                            {group.tricks.length} tricks
                          </span>
                        </div>
                        <div className="min-w-0 text-[10px] sm:text-[11px] text-zinc-400 font-orbitron uppercase tracking-[0.12em] sm:tracking-[0.16em] break-words">
                          {isExpanded ? 'Click To Collapse / 点击收起' : 'Click To Expand / 点击展开'}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="mt-4 space-y-3">
                          {group.tricks.map((trick, index) => {
                            const isEditing = editingTrickId === trick.id;

                            return (
                              <div key={trick.id} className="min-w-0 rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                                <div className="grid min-w-0 gap-3 lg:grid-cols-[90px_minmax(0,1fr)_120px_auto] lg:items-center">
                                  <div className="text-cyan-300 font-orbitron text-sm">
                                    #{index + 1}
                                    <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-zinc-500">in group</span>
                                  </div>
                                  {isEditing ? (
                                    <input
                                      value={editingTrickName}
                                      onChange={e => setEditingTrickName(e.target.value)}
                                      className="rounded-xl border border-cyan-400/40 bg-black/50 px-4 py-3 text-white outline-none"
                                    />
                                  ) : (
                                    <div className="text-white font-rajdhani text-lg break-words">{trick.name}</div>
                                  )}
                                  {isEditing ? (
                                    <input
                                      value={editingTrickScore}
                                      onChange={e => setEditingTrickScore(e.target.value)}
                                      className="rounded-xl border border-cyan-400/40 bg-black/50 px-4 py-3 text-white outline-none"
                                    />
                                  ) : (
                                    <div className="text-yellow-300 font-orbitron text-lg">{trick.score} pts</div>
                                  )}
                                  <div className="flex items-center gap-2 justify-start lg:justify-end">
                                    {isEditing ? (
                                      <button
                                        type="button"
                                        onClick={handleSaveTrick}
                                        className="p-2 rounded-lg border border-green-500/30 bg-green-500/10 text-green-300 hover:bg-green-500/20 transition-colors"
                                        title="Save trick"
                                      >
                                        <Check size={16} />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => startEditingTrick(trick)}
                                        className="p-2 rounded-lg border border-white/15 bg-white/[0.03] text-zinc-300 hover:text-white hover:border-cyan-400/30 transition-colors"
                                        title="Edit trick"
                                      >
                                        <Pencil size={16} />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteTrick(trick.id)}
                                      className="p-2 rounded-lg border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                                      title="Delete trick"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      </>
      )}

      {mode === 'draw' && (
      <>
      <div className="rounded-2xl border border-fuchsia-500/20 bg-black/40 p-4 md:p-5">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-fuchsia-300 font-orbitron text-sm tracking-[0.22em] uppercase">
              <Layers size={16} />
              Draw Preset Decks / 抽招赛默认总表
            </div>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              Manage the global default master lists used by Draw Battle mode. Changes here become the shared preset draw decks for all users. / 在这里管理抽招赛默认总表，保存后会成为所有用户共用的抽招赛默认总表。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleResetDraw}
              disabled={!hasDrawUnsavedChanges || drawSaving}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-orbitron text-xs uppercase tracking-wider"
            >
              <RotateCcw size={14} />
              Reset / 重置
            </button>
            <button
              onClick={handleSaveDrawPresetDecks}
              disabled={!hasDrawUnsavedChanges || drawSaving}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 text-fuchsia-200 hover:bg-fuchsia-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-orbitron text-xs uppercase tracking-wider"
            >
              <Save size={14} />
              {drawSaving ? 'Saving / 保存中' : 'Save Changes / 保存更改'}
            </button>
          </div>
        </div>
        {drawSaveMessage && (
          <div className="mt-4 rounded-xl border border-green-500/25 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0" />
            <span>{drawSaveMessage}</span>
          </div>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <div className="rounded-2xl border border-white/10 bg-black/35 p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm md:text-base font-orbitron tracking-[0.2em] uppercase text-white">Draw Lists / 抽招总表</h2>
            <span className="text-[10px] text-zinc-500 font-orbitron tracking-[0.18em] uppercase">{drawPresetDecks.length} Lists</span>
          </div>

          <form onSubmit={handleCreateDrawDeck} className="space-y-2">
            <input
              value={newDrawDeckName}
              onChange={e => setNewDrawDeckName(e.target.value)}
              placeholder="New draw list... / 新抽招总表..."
              className="w-full rounded-lg border border-white/10 bg-black/50 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400/60"
            />
            <div className="flex gap-2">
              <select
                value={newDrawDeckFolderId}
                onChange={e => setNewDrawDeckFolderId(e.target.value)}
                className="flex-1 rounded-lg border border-white/10 bg-black/50 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400/60"
              >
                {PRESET_FOLDERS.map(folder => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
              </select>
              <button
                type="submit"
                className="px-3 py-2 rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/20 transition-colors"
                title="Create draw list"
              >
                <Plus size={16} />
              </button>
            </div>
          </form>

          <div className="space-y-4 max-h-[520px] overflow-y-auto custom-scrollbar pr-1">
            {loading ? (
              <div className="py-10 text-center text-zinc-500 font-orbitron text-sm">Loading draw preset decks...</div>
            ) : drawPresetDecks.length === 0 ? (
              <div className="py-10 text-center text-zinc-500 font-orbitron text-sm">No draw preset decks found.</div>
            ) : groupedDrawPresetDecks.map(group => {
              const isExpanded = !!expandedDrawFolders[group.id];

              return (
                <section key={group.id} className="rounded-2xl border border-white/8 bg-white/[0.02] p-3">
                  <button
                    type="button"
                    onClick={() => toggleDrawFolder(group.id)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-fuchsia-400/20 hover:bg-white/[0.045]"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-white font-rajdhani font-bold">
                        {isExpanded ? (
                          <ChevronDown size={16} className="shrink-0 text-fuchsia-300" />
                        ) : (
                          <ChevronRight size={16} className="shrink-0 text-fuchsia-300" />
                        )}
                        <span className="truncate">{group.name}</span>
                      </div>
                      <div className="mt-1 text-[10px] font-orbitron uppercase tracking-[0.18em] text-zinc-500">
                        {isExpanded ? 'Sub Groups Visible / 小组已展开' : 'Click To Expand / 点击展开'}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-fuchsia-300 font-orbitron text-xs">{group.decks.length}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.14em]">Lists</div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="mt-3 space-y-2 pl-2">
                      {group.decks.map(deck => (
                        <button
                          type="button"
                          key={deck.id}
                          onClick={() => setSelectedDrawDeckId(deck.id)}
                          className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                            selectedDrawDeck?.id === deck.id
                              ? 'border-fuchsia-400/40 bg-fuchsia-500/10'
                              : 'border-white/8 bg-white/[0.03] hover:border-fuchsia-400/20'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-white font-rajdhani font-bold truncate">{deck.name}</div>
                              <div className="mt-1 text-[10px] font-orbitron uppercase tracking-[0.18em] text-zinc-500">
                                Shared Draw Default / 共用抽招默认表
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-fuchsia-300 font-orbitron text-xs">{deck.tricks.length}</div>
                              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.14em]">Tricks</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/35 p-4 md:p-5 space-y-4">
          {!selectedDrawDeck ? (
            <div className="py-16 text-center text-zinc-500 font-orbitron">Select a draw list / 请选择一个抽招总表</div>
          ) : (
            <>
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_auto] lg:items-end">
                <div className="min-w-0">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-zinc-500">List Name / 总表名称</div>
                  <input
                    value={selectedDrawDeck.name}
                    onChange={e => {
                      updateSelectedDrawDeck(deck => ({ ...deck, name: e.target.value }));
                      setDrawSaveMessage('');
                    }}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-white font-rajdhani text-xl outline-none focus:border-fuchsia-400/60"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-zinc-500">Folder / 文件夹</div>
                  <select
                    value={selectedDrawDeck.folderId || PRESET_FOLDERS[0]?.id || ''}
                    onChange={e => {
                      const nextFolderId = e.target.value;
                      updateSelectedDrawDeck(deck => ({ ...deck, folderId: nextFolderId }));
                      setExpandedDrawFolders(previous => ({
                        ...previous,
                        [nextFolderId]: true
                      }));
                      setDrawSaveMessage('');
                    }}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-white outline-none focus:border-fuchsia-400/60"
                  >
                    {PRESET_FOLDERS.map(folder => (
                      <option key={folder.id} value={folder.id}>{folder.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => handleDeleteDrawDeck(selectedDrawDeck.id)}
                  className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors font-orbitron text-xs uppercase tracking-wider"
                >
                  <Trash2 size={14} />
                  Delete List / 删除总表
                </button>
              </div>

              <form onSubmit={handleAddDrawTrick} className="rounded-2xl border border-fuchsia-500/15 bg-fuchsia-500/[0.05] p-4">
                <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-fuchsia-300/80">Add Trick / 新增招式</div>
                <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                  <input
                    value={newDrawTrickName}
                    onChange={e => setNewDrawTrickName(e.target.value)}
                    placeholder="Trick name / 招式名称"
                    className="rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-white outline-none focus:border-fuchsia-400/60"
                  />
                  <button
                    type="submit"
                    className="rounded-xl border border-fuchsia-400/35 bg-fuchsia-500/15 px-4 py-3 text-fuchsia-200 hover:bg-fuchsia-500/25 transition-colors font-orbitron text-xs uppercase tracking-wider"
                  >
                    Add / 添加
                  </button>
                </div>
              </form>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-zinc-500">Trick List / 招式列表</div>
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.18em] text-fuchsia-300/80">
                    {selectedDrawDeckWithPendingEdit?.tricks.length || 0} tricks
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  {!selectedDrawDeckWithPendingEdit || selectedDrawDeckWithPendingEdit.tricks.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] py-10 text-center text-zinc-500 font-orbitron text-sm">
                      No tricks in this draw list yet. / 当前抽招总表还没有招式。
                    </div>
                  ) : (
                    selectedDrawDeckWithPendingEdit.tricks.map((trick, index) => {
                      const isEditing = editingDrawTrickIndex === index;

                      return (
                        <div key={`${selectedDrawDeckWithPendingEdit.id}-${index}`} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                          <div className="grid gap-3 lg:grid-cols-[90px_minmax(0,1fr)_auto] lg:items-center">
                            <div className="text-fuchsia-300 font-orbitron text-sm">
                              #{index + 1}
                              <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-zinc-500">trick</span>
                            </div>
                            {isEditing ? (
                              <input
                                value={editingDrawTrickName}
                                onChange={e => setEditingDrawTrickName(e.target.value)}
                                className="rounded-xl border border-fuchsia-400/40 bg-black/50 px-4 py-3 text-white outline-none"
                              />
                            ) : (
                              <div className="text-white font-rajdhani text-lg break-words">{trick}</div>
                            )}
                            <div className="flex items-center gap-2 justify-start lg:justify-end">
                              {isEditing ? (
                                <button
                                  type="button"
                                  onClick={handleSaveDrawTrick}
                                  className="p-2 rounded-lg border border-green-500/30 bg-green-500/10 text-green-300 hover:bg-green-500/20 transition-colors"
                                  title="Save trick"
                                >
                                  <Check size={16} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => startEditingDrawTrick(index, trick)}
                                  className="p-2 rounded-lg border border-white/15 bg-white/[0.03] text-zinc-300 hover:text-white hover:border-fuchsia-400/30 transition-colors"
                                  title="Edit trick"
                                >
                                  <Pencil size={16} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteDrawTrick(index)}
                                className="p-2 rounded-lg border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                                title="Delete trick"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default AdminTournamentMasterLists;
